import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {ProgressSpinner} from 'primereact/progressspinner';
import {Message} from 'primereact/message';
import {Button} from 'primereact/button';

import {MarketplaceProductService} from '../../service/MarketplaceProductService';
import {AMAZON_OAUTH_DEFAULT_RETURN_HASH, AMAZON_OAUTH_RETURN_HASH_KEY} from '../../utils/amazonOAuth';

const marketplaceProductService = new MarketplaceProductService();

// How long the success message stays up before the redirect back to the
// wizard, so the seller sees confirmation rather than an instant jump.
const SUCCESS_REDIRECT_DELAY_MS = 1500;

// Landing page for Amazon's OAuth redirect: https://app.pricerest.com/amazon/callback
// This is a REAL top-level browser navigation, not an in-app route -- Amazon
// sends the browser here directly, with spapi_oauth_code/state/
// selling_partner_id as query params and NO hash. server.js's catch-all
// serves index.html for this path like any other, but the app otherwise
// navigates entirely through HashRouter, whose routes only ever match the
// hash -- which is empty here. So this component is never reached through a
// <Route>: index.js renders it directly, standalone, bypassing the router
// entirely, whenever window.location.pathname is this exact path (see
// index.js). It reads the query string itself rather than relying on
// react-router for that reason.
const AmazonOAuthCallback = () => {
    const {t} = useTranslation();
    const [status, setStatus] = useState('working'); // 'working' | 'success' | 'error'
    const [errorMessage, setErrorMessage] = useState('');

    const goBack = () => {
        let returnHash = AMAZON_OAUTH_DEFAULT_RETURN_HASH;
        try {
            returnHash = sessionStorage.getItem(AMAZON_OAUTH_RETURN_HASH_KEY) || returnHash;
            sessionStorage.removeItem(AMAZON_OAUTH_RETURN_HASH_KEY);
        } catch (e) { /* private browsing / storage disabled -- fall back to the default */ }
        window.location.href = '/' + returnHash;
    };

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const code = params.get('spapi_oauth_code');
        const state = params.get('state');
        const sellingPartnerId = params.get('selling_partner_id');
        if (!code || !state) {
            setStatus('error');
            setErrorMessage(t('amazonOauth.missingParams'));
            return;
        }
        marketplaceProductService.completeAmazonOAuth(state, code, sellingPartnerId).then(() => {
            setStatus('success');
            setTimeout(goBack, SUCCESS_REDIRECT_DELAY_MS);
        }).catch(err => {
            const data = err && err.response && err.response.data;
            setStatus('error');
            setErrorMessage((data && data.error) || t('amazonOauth.genericError'));
        });
        // Runs once: this page has exactly one query string to read and one
        // callback to complete, ever -- there is nothing to react to again.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            minHeight: '100vh', background: '#f9fafb', padding: 16, boxSizing: 'border-box'}}>
            <div style={{
                maxWidth: 420, width: '100%', padding: 28, background: '#fff', borderRadius: 12,
                boxShadow: '0 2px 16px rgba(0,0,0,.08)', textAlign: 'center'}}>
                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 18}}>
                    <span style={{
                        width: 34, height: 34, borderRadius: 9, background: '#ff9900', color: '#fff',
                        fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>A</span>
                    <h2 style={{margin: 0, fontSize: '1.1rem', color: '#111827'}}>{t('amazonOauth.title')}</h2>
                </div>

                {status === 'working' &&
                    <div>
                        <ProgressSpinner style={{width: 44, height: 44}} strokeWidth="4" animationDuration=".6s"/>
                        <p style={{color: '#6b7280', marginTop: 12}}>{t('amazonOauth.working')}</p>
                    </div>}

                {status === 'success' &&
                    <Message severity="success" text={t('amazonOauth.success')} style={{width: '100%'}}/>}

                {status === 'error' &&
                    <>
                        <Message severity="error" text={errorMessage} style={{width: '100%'}}/>
                        <div style={{marginTop: 18}}>
                            <Button label={t('amazonOauth.back')} onClick={goBack} style={{width: '100%'}}/>
                        </div>
                    </>}
            </div>
        </div>
    );
};

export default AmazonOAuthCallback;
